// ref: https://confluence.zhihuiya.com/pages/viewpage.action?pageId=124543238

import type { EActivityTriggerType } from '../types/activity-type';

export enum E_ActivityType {
  /**
   * 邀请注册
   */
  inviteRegister = 'inviteRegisterV2',
  /**
   * 反馈
   */
  inviteFeedback = 'inviteFeedback',
  /**
   * 完善信息
   */
  perfectInformation = 'perfectInformation',
}

export enum E_ActivityStatus {
  unfinished = 'UNFINISHED',
  unreceived = 'UNRECEIVED',
  received = 'RECEIVED',
}

export interface IActivityItem {
  enable: boolean;
  activity_type: E_ActivityType | string;
}

export interface IActivityReceiveItem {
  receive_type: E_ActivityType;
  receive_status: E_ActivityStatus;
}

export type IGetActivityInitData = IActivityItem[];

export interface IInviteRegisterItem {
  /**
   * 活动类型
   */
  activity_type: string;
  /**
   * 活动邀请人数上限
   */
  invite_limit: number;
  /**
   * 已成功邀请人数
   */
  invite_num: number;
  /**
   * 邀请链接，链接中包含如下字段
   * channel_source=personInviteRegister
   * activity_type=inviteRegister
   * inviter_id={userId}
   * invitation_code=XXX
   */
  invite_link: string;
  /**
   * 账号到期日 yyyy-MM-dd
   */
  time_end: string;
}

export interface IUserInformation {
  need_company?: boolean;
  company: string;
  need_mobile?: boolean;
  mobile?: string;
  code?: string;
  trigger_manually?: boolean;
}

export interface IClueForm {
  company_name: string;
  post: string;
  name: string;
  mobile: string;
  code: string;
  reason: string[];
  otherReason: string;
}

export interface IClueInfo extends IClueForm {
  type: EurekaPackageType;
  source_type?: EActivityTriggerType;
}

export const ClueReasonOther = 'other';

export interface IAmountItem {
  key: string;
  isDivider?: boolean;
  support?: boolean;
  max_amount?: number;
  current_amount?: number;
  capacity_dimension?: string;
  group_end_at?: string;
  end_at?: string;
  type?: string;
  dimension?: string;
  has_share_capacity?: boolean;
  children?: IAmountItem[];
}

export type IGetInviteRegisterInfoData = IInviteRegisterItem;

/**
 * /account-privilege/need-add-trial 返回的 actionType 枚举（新版，原来返回 boolean）
 */
export enum NeedAddTrialActionType {
  /** 当前用户有权限，直接进入产品 */
  DIRECT_LOGIN = 'direct_login',
  /** 个人租户已存在且有目标产品权限，引导用户切换到个人租户 */
  SWITCH_TO_INDIVIDUAL_TENANT = 'switch_to_individual_tenant',
  /** 个人租户已存在但没有目标产品权限，引导切换后再完成赋权流程 */
  SWITCH_AND_GRANT_PRIVILEGE = 'switch_and_grant_privilege',
  /** 已是个人租户但无目标产品权限，后端已在本次请求中直接完成赋权，前端按"赋权成功，进入产品"处理 */
  GRANT_INDIVIDUAL_TENANT_PRIVILEGE = 'grant_individual_tenant_privilege',
  /** 企业允许创建个人租户，但个人租户不存在，引导用户创建/切换个人租户 */
  CREATE_INDIVIDUAL_TENANT = 'create_individual_tenant',
  /** 企业不允许创建个人租户，提示联系管理员 */
  CONTACT_ADMIN = 'contact_admin',
}

/** need-add-trial 新版响应体（原来是 boolean，现在是包含 actionType 的对象） */
export interface INeedAddTrialResponse {
  actionType: NeedAddTrialActionType | string;
}

/**
 * 自动赋权接口
 */
export interface IAddTrialExtendBody {
  /**
   * 账号注册来源
   */
  channel_source: string;
  /**
   * 活动类型
   */
  activity_type: string;
  /**
   * 邀请人id
   */
  inviter_id: string;
  /**
   * 企业邀请code 企业邀请注册时有值
   */
  invitation_code: string;
  /**
   * 邀请人邀请码
   */
  media_type: 'Mobile' | '';
  /**
   * 邀请注册来源
   */
  from?: string;
}

export interface IPostSubmitFeedbackParams {
  content: string;
}

/**
 * 发送验证码
 */
export enum E_MobileVerifyCodePurpose {
  LOGIN = 'LOGIN',
  LOGIN_REGISTER = 'LOGIN_REGISTER',
  MOBILE_NUMBER_BINDING = 'MOBILE_NUMBER_BINDING',
  UNIONID_BINDING = 'UNIONID_BINDING',
  PASSWORD_FORGOT = 'PASSWORD_FORGOT',
  REGISTER = 'REGISTER',
  SSO_USER_PROFILE = 'SSO_USER_PROFILE',
  SMS_EUREKA = 'SMS_EUREKA',
  SMS_MOBILE_NUMBER_UPDATE = 'SMS_MOBILE_NUMBER_UPDATE',
  SMS_SALES_LEAD = 'SMS_SALES_LEAD',
}

/**
 * 新套餐版本
 */
export enum EurekaPackageType {
  PROTECT_FREE = 'protect_free',
  PROTECT_TRIAL = 'protect_trial',
  PROTECT_COMMERCIAL_OLD = 'protect_commercial_old',
  PROTECT_COMMERCIAL = 'protect_commercial',

  IP_COMMERCIAL = 'ip_commercial',

  MATERIAL_TRIAL = 'material_trial',
  MATERIAL_COMMERCIAL_OLD = 'material_commercial_old',
  MATERIAL_COMMERCIAL = 'material_commercial',
  MATERIAL_COMMERCIAL_RAPID_SEARCH = 'material_commercial_rapid_search',

  AIR_FREE = 'air_free',
  AIR_TRIAL = 'air_trial',
  AIR_COMMERCIAL = 'air_commercial',
  AIR_COMMERCIAL_PROFESSIONAL_EDITION = 'air_commercial_professional_edition',
  AIR_COMMERCIAL_NAVIGATOR_EDITION = 'air_commercial_navigator_edition',

  INSIGHTS_TRIAL = 'insights_trial',
  INSIGHTS_COMMERCIAL = 'insights_commercial',
  LS_COMMERCIAL = 'ls_commercial',
  LS_COMMERCIAL_SHARE = 'ls_commercial_share',
  LS_COMMERCIAL_AGENT = 'ls_commercial_agent',
  LS_COMMERCIAL_PDA_USAGE_PACKAGE = 'ls_commercial_pda_usage_package',

  RD_PERSONAL_COMMERCIAL = 'rd_personal_commercial',
  AGENT_PERSONAL_COMMERCIAL = 'agent_personal_commercial',
}

/**
 * 套餐基础信息
 */
export interface IPackageType {
  end_at: EurekaPackageType;
  eureka_package: string;
  trial_package_left_days?: number;
  product_type: string;
  package_type: 'FREE' | 'TRIAL' | 'COMMERCIAL';
}

export interface ISaveUserInfo {
  company: string;
  mobile?: string;
  country_code?: string;
  trigger_manually: boolean;
  role: string;
  from: 'eureka_home' | 'patent_view';
}

export interface AccountInfoCn {
  company_name: string;
  mobile_number: string;
  full_name: string;
  job_role: string;
}

export interface AccountInfoUs {
  company_name: string;
  mobile_number: string;
  first_name: string;
  last_name: string;
  email: string;
  industry: string;
  country_code: string;
}

export interface AccountInfo extends AccountInfoCn, AccountInfoUs {}

export interface AccountInfoSave extends AccountInfo {
  manual_company: string;
  code: string;
}

/**
 * 套餐版本
 */
export enum EUserPackage {
  GENERAL_FREEMIUM = 'GENERAL_FREEMIUM', // 范域个人版
  GENERAL_TRIAL = 'GENERAL_TRIAL', // 范域试用版
  GENERAL_ENTERPRISE = 'GENERAL_ENTERPRISE', // 范域企业版
  GENERAL_UNIVERSITY = 'GENERAL_UNIVERSITY', // 范域高校版
  MATERIAL_TRIAL = 'MATERIAL_TRIAL', // 材料试用版
  MATERIAL_ENTERPRISE = 'MATERIAL_ENTERPRISE', // 材料企业版
  AIR_FREEMIUM = 'AIR_FREEMIUM', // Air 基础版
  AIR_TRIAL = 'AIR_TRIAL', // Air 试用版
  AIR_ENTERPRISE = 'AIR_ENTERPRISE', // Air 企业版
  // ... 其他套餐
  NEV_TRIAL = 'NEV_TRIAL',
}

export interface AddTrialInput {
  fromSite?: string;
  extendBody?: IAddTrialExtendBody;
}

export interface LsPlgUsage {
  user_id: string;
  role_expire_at: string;
  role_package: 'enterprise' | 'team' | 'personal_pro' | 'free_trial' | 'personal_basic';
  usage: {
    key: 'project_storage' | 'privilege_credit' | 'reword_credit';
    type: string;
    capacity_dimension: 'day' | 'month' | 'all';
    support: boolean;
    max_amount: number;
    current_amount: number;
  }[];
}

export type ExperimentPriceKey = 'ls_plg_price';
export type ExperimentPriceValue = 'economy' | 'standard';

export interface IExperimentItem<K, V> {
  experiment_key: K;
  experiment_group: V;
}
